/**
 *
 * ManageSubscriptionButton
 *
 * Sends the customer into the Stripe Customer Portal, where they can add or
 * update a card, cancel (at period end), and read their invoice history.
 *
 * Two surfaces render it — the trial countdown banner and the account page — so
 * the behaviour lives here rather than being written twice. Only the styling
 * differs, which each parent supplies through `className` (styled-components
 * wraps this component), so the two can look right in their own context while
 * the thing they DO cannot drift.
 *
 * A container rather than a component, by this repo's own boundary (README):
 * containers reach application state and trigger actions, components render
 * what they are handed. This reads the session token and mints a portal
 * session, so it belongs on this side of the line — every one of the 40-odd
 * entries under `src/components` is prop-driven, and nothing there touches the
 * store.
 *
 * It is a full-page redirect, not a new tab. A customer sent to manage billing
 * is doing one task; a popup would be blocked by some browsers and orphaned by
 * others, and Stripe returns them to the dashboard when they are done.
 */
import React from 'react';
import { toast } from 'sonner';
import { t } from 'copy/t';
import { useBillingPortalSession } from 'containers/App/api/mutations';

const GENERIC_ERROR = t("toast.billing.genericError");

const ManageSubscriptionButton = ({
  className,
  children = 'Manage subscription',
}) => {
  const { mutate, isLoading } = useBillingPortalSession({
    onSuccess: (data) => {
      // A 2xx without a url is not success in any sense the customer cares
      // about, and navigating to `undefined` would take them to a 404 on our
      // own domain — worse than staying put with an explanation.
      if (!data || !data.url) {
        toast.error(GENERIC_ERROR);
        return;
      }
      window.location.href = data.url;
    },
    // Covers the 404 while the rails endpoint is still unshipped, and the
    // rejection for an account with no Stripe customer behind it (a legacy
    // subscription billed outside Stripe). Both leave the customer where they
    // were, told what happened.
    onError: (error) => toast.error((error && error.error) || GENERIC_ERROR),
  });

  return (
    <button
      type="button"
      className={className}
      onClick={() => mutate()}
      // Minting a portal session is a round trip, and the click is followed by
      // a full-page navigation — so a second click would mint a second session
      // and race the first one's redirect.
      disabled={isLoading}
    >
      {isLoading ? 'Opening…' : children}
    </button>
  );
};

export default ManageSubscriptionButton;
